/* eslint-disable max-lines */
import {
  changeLanguageMatcherType,
  editLanguageMatcher,
  fetchLanguageMatcherTypes,
} from 'actions/identifier';
import SelectRedesign from 'components/Select/SelectRedesign';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getCompareMode,
  getLanguageMatcherTypes,
  getOriginalActiveLanguageMatcher,
  getShouldDisableEdit,
} from 'selectors/identifier';
import { useSelector } from 'store';

type MatcherGroup = 'unset' | 'general' | 'metadata' | 'money' | 'raw_regex';

const matcherGroupLabel: Record<MatcherGroup, string> = {
  unset: 'Select matcher',
  general: 'General',
  metadata: 'Metadata',
  money: 'Money',
  raw_regex: 'Raw Regex',
};

const deriveMatcherGroup = (type?: string): MatcherGroup => {
  if (!type) return 'unset';
  if (type === 'money') return 'money';
  if (type === 'raw_regex') return 'raw_regex';
  if (type.startsWith('metadata_')) return 'metadata';
  return 'general';
};

type Props = {
  showTypeSelector?: boolean;
};

const LanguageMatcherHeader: React.FC<Props> = ({ showTypeSelector = true }) => {
  const dispatch = useDispatch();

  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const originalActiveLanguageMatcher = useSelector(getOriginalActiveLanguageMatcher);
  const combinations = useSelector(getLanguageMatcherTypes);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);

  const [combinationSelection, setCombinationSelection] = useState<string>('');
  const [matcherGroup, setMatcherGroup] = useState<MatcherGroup>('unset');
  const isLanguageMatcherNew = Boolean(activeLanguageMatcher?.isNew);

  useEffect(() => {
    dispatch(fetchLanguageMatcherTypes());
  }, [dispatch]);

  useEffect(() => {
    if (!activeLanguageMatcher) return;
    const activeType = activeLanguageMatcher.type || '';

    if (activeType === 'metadata_communication') {
      const variant = activeLanguageMatcher.__metadata_variant as string | undefined;
      setCombinationSelection(
        variant === 'general' ? 'metadata_general' : 'metadata_communication_type'
      );
    } else if (activeType) {
      setCombinationSelection(activeType);
    }

    const nextGroup = deriveMatcherGroup(activeType);
    if (nextGroup !== 'unset') setMatcherGroup(nextGroup);
  }, [activeLanguageMatcher]);

  const onEditLanguageMatcher = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!activeLanguageMatcher?.uuid || e.target.value === null) return;
    dispatch(
      editLanguageMatcher({
        languageMatcherId: activeLanguageMatcher.uuid,
        data: { [e.target.name]: e.target.value },
      })
    );
  };

  const applyLanguageMatcherType = (nextType: string): void => {
    if (!activeLanguageMatcher?.uuid) return;
    const defaultValues: { [key: string]: string | number | boolean | string[] } = {};
    Object.entries(combinations[nextType as keyof typeof combinations]).forEach(([key, field]) => {
      if (field != null && field.default) {
        defaultValues[key] = field.default;
      }
    });
    dispatch(
      changeLanguageMatcherType({
        languageMatcherId: activeLanguageMatcher.uuid,
        type: nextType,
        ...defaultValues,
      })
    );
  };

  const setMetadataVariant = (variant: 'general' | 'communication'): void => {
    if (!activeLanguageMatcher?.uuid) return;
    dispatch(
      editLanguageMatcher({
        languageMatcherId: activeLanguageMatcher.uuid,
        // UI-only helper field
        data: { __metadata_variant: variant } as unknown as Record<string, unknown>,
      })
    );
  };

  const applyDisplayedTypeSelection = (val: string): void => {
    if (!val) return;
    if (!activeLanguageMatcher?.uuid) return;

    setCombinationSelection(val);

    if (val === 'metadata_general') {
      setMetadataVariant('general');
      dispatch(
        editLanguageMatcher({
          languageMatcherId: activeLanguageMatcher.uuid,
          data: {
            communication_kind: activeLanguageMatcher.communication_kind || [],
          } as unknown as Record<string, unknown>,
        })
      );
      applyLanguageMatcherType('metadata_communication');
      return;
    }
    if (val === 'metadata_communication_type') {
      setMetadataVariant('communication');
      dispatch(
        editLanguageMatcher({
          languageMatcherId: activeLanguageMatcher.uuid,
          data: {
            key: undefined,
            values: [],
            platform: undefined,
            is_list: false,
          } as unknown as Record<string, unknown>,
        })
      );
      applyLanguageMatcherType('metadata_communication');
      return;
    }

    applyLanguageMatcherType(val);
  };

  const generalTypeOptions = [
    { value: 'token', label: 'Token' },
    { value: 'token_with_modifier', label: 'Pointer' },
    { value: 'adjacent_tokens', label: 'Adjacent token' },
    { value: 'regex', label: 'Regex' },
  ].filter((o) => Boolean(combinations[o.value as keyof typeof combinations]));

  const metadataTypeOptions = [
    { value: 'metadata_general', label: 'General' },
    { value: 'metadata_communication_type', label: 'Communication' },
    { value: 'metadata_length', label: 'Length' },
    { value: 'metadata_recipient', label: 'Recipient' },
    { value: 'metadata_sender', label: 'Sender' },
  ].filter((o) => {
    if (o.value === 'metadata_general' || o.value === 'metadata_communication_type') {
      return Boolean(combinations.metadata_communication);
    }
    return Boolean(combinations[o.value as keyof typeof combinations]);
  });

  const typeOptionsByGroup: Record<MatcherGroup, { value: string; label: string }[]> = {
    unset: [],
    general: generalTypeOptions,
    metadata: metadataTypeOptions,
    money: [{ value: 'money', label: 'Money' }].filter((o) =>
      Boolean(combinations[o.value as keyof typeof combinations])
    ),
    raw_regex: [{ value: 'raw_regex', label: 'None' }].filter((o) =>
      Boolean(combinations[o.value as keyof typeof combinations])
    ),
  };

  const visibleTypeOptions = typeOptionsByGroup[matcherGroup];
  const allTypeOptions = Object.values(typeOptionsByGroup).flat();

  const selectedTypeLabel =
    allTypeOptions.find((o) => o.value === combinationSelection)?.label || combinationSelection;

  const onChangeMatcherGroupValue = (nextGroup: MatcherGroup): void => {
    setMatcherGroup(nextGroup);

    if (nextGroup === 'money') {
      applyLanguageMatcherType('money');
      return;
    }
    if (nextGroup === 'raw_regex') {
      applyLanguageMatcherType('raw_regex');
      return;
    }

    const nextOptions = typeOptionsByGroup[nextGroup] ?? [];
    if (nextOptions.some((o) => o.value === combinationSelection)) return;

    const fallback = nextOptions[0]?.value ?? '';
    if (!fallback) {
      setCombinationSelection('');
      return;
    }
    applyDisplayedTypeSelection(fallback);
  };

  if (!activeLanguageMatcher) return null;

  const description = ((): string => {
    const descKey =
      combinationSelection === 'metadata_general' ||
      combinationSelection === 'metadata_communication_type'
        ? 'metadata_communication'
        : combinationSelection;
    // @ts-ignore
    return combinations[descKey as keyof typeof combinations]?.description || '';
  })();

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor="name" className="flex flex-col gap-1 text-body w-124">
        <div className="flex flex-row">
          <span>Name</span>
          <span className="text-litlingo-alert">*</span>
        </div>
        <input
          name="name"
          id="name"
          data-testid="matcher-name-input"
          value={activeLanguageMatcher.name}
          className={`form-input w-full py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none ${
            activeLanguageMatcher.name !== originalActiveLanguageMatcher?.name && compareMode
              ? 'bg-litlingo-focus-area-background'
              : ''
          }`}
          onChange={onEditLanguageMatcher}
          disabled={compareMode || shouldDisable}
        />
      </label>

      <div className="w-124">
        <div className="flex flex-row gap-4">
          <div className="w-1/2">
            <div className="flex flex-col gap-1 text-body">
              <div>Matcher</div>
              {!isLanguageMatcherNew ? (
                <div className="py-2 px-4 w-full bg-litlingo-gray-0.5 border border-litlingo-gray-2 rounded-lg">
                  {matcherGroupLabel[matcherGroup]}
                </div>
              ) : (
                <SelectRedesign
                  dataTestid="select-language-matcher-matcher"
                  className="h-9.5 rounded-lg bg-white"
                  valueClassName="h-9.5 py-2 px-2"
                  options={(
                    ['unset', 'general', 'metadata', 'money', 'raw_regex'] as MatcherGroup[]
                  ).map((g) => ({
                    label: matcherGroupLabel[g],
                    value: g,
                    disabled: compareMode || shouldDisable,
                  }))}
                  onChange={(value): void => {
                    onChangeMatcherGroupValue(value as MatcherGroup);
                  }}
                  value={matcherGroup}
                  disabled={compareMode || shouldDisable}
                />
              )}
            </div>
          </div>

          {showTypeSelector && matcherGroup !== 'unset' && matcherGroup !== 'money' && (
            <div className="w-1/2">
              <div className="flex flex-col gap-1 text-body">
                <div>Type</div>
                {!isLanguageMatcherNew ? (
                  <div className="py-2 px-4 w-full bg-litlingo-gray-0.5 border border-litlingo-gray-2 rounded-lg">
                    {selectedTypeLabel}
                  </div>
                ) : (
                  <div>
                    <SelectRedesign
                      dataTestid="select-language-matcher-type"
                      className="h-9.5 rounded-lg bg-white"
                      valueClassName="h-9.5 py-2 px-2"
                      placeholder="Select type"
                      options={visibleTypeOptions.map((o) => ({
                        label: o.label,
                        value: o.value,
                        disabled: compareMode || shouldDisable || matcherGroup === 'raw_regex',
                      }))}
                      onChange={(value): void => applyDisplayedTypeSelection(value)}
                      value={combinationSelection}
                      disabled={compareMode || shouldDisable || matcherGroup === 'raw_regex'}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {description && <p className="mt-2 text-sm text-gray-500">{description}</p>}
      </div>
    </div>
  );
};

export default LanguageMatcherHeader;
